import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hasBrandSelection } from '../../utils/brand-selector';
import { environment } from '../../config';

type BrandSelectionGuardProps = {
  children: React.ReactNode;
};

/**
 * Guard component that redirects to brand selector if no brand is selected in development mode
 */
export default function BrandSelectionGuard({ children }: BrandSelectionGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only run in development mode
    if (!environment.debugMode) {
      return;
    }

    // Don't redirect if we're already on the brand selector page
    if (location.pathname === '/dev/brand') {
      return;
    }

    // Don't redirect if we're on other dev/admin pages that shouldn't be protected
    if (location.pathname.startsWith('/oauth2/') || location.pathname === '/404') {
      return;
    }

    // Check if a brand has been selected
    if (!hasBrandSelection()) {
      console.log('No brand selected in development mode, redirecting to brand selector');
      navigate('/dev/brand', { replace: true });
      return;
    }
  }, [navigate, location.pathname]);

  // In development mode, if no brand is selected and we're not on the brand selector page,
  // show a loading state while redirect happens
  if (environment.debugMode && !hasBrandSelection() && location.pathname !== '/dev/brand') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center">
        <span className="icon-[fluent--spinner-ios-20-filled] animate-spin text-blue-600 h-8 w-8 mb-4" />
        <p className="text-gray-600">Redirecting to brand selector...</p>
      </div>
    );
  }

  return <>{children}</>;
}

